import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Calendar,
  Clock,
  ArrowRight,
  ArrowLeft,
  Users,
  Loader2,
} from 'lucide-react';
import {
  getContactActivityRequest,
  selectContactActivities,
  selectContactActivityLoading,
  selectContactActivityError,
  selectContactActivityTotal,
} from 'store/contacts';

interface ActivityTabProps {
  contactId: string;
}

const ActivityTab: React.FC<ActivityTabProps> = ({ contactId }) => {
  const dispatch = useDispatch();
  const activities = useSelector(selectContactActivities);
  const loading = useSelector(selectContactActivityLoading);
  const error = useSelector(selectContactActivityError);
  const totalActivities = useSelector(selectContactActivityTotal);

  useEffect(() => {
    if (contactId) {
      dispatch(getContactActivityRequest(contactId));
    }
  }, [contactId, dispatch]);
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  };

  const getActivityIcon = (type: 'add' | 'remove') => {
    return type === 'add' ? (
      <ArrowRight className="w-3 h-3 text-success" />
    ) : (
      <ArrowLeft className="w-3 h-3 text-destructive" />
    );
  };

  const getActivityColor = (type: 'add' | 'remove') => {
    return type === 'add'
      ? 'bg-success-background border-success/30'
      : 'bg-destructive-background border-destructive/30';
  };

  const getActivityTextColor = (type: 'add' | 'remove') => {
    return type === 'add' ? 'text-green-800' : 'text-red-800';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Activity Timeline
          </h3>
          <p className="text-xs text-gray-500">
            Track contact's segment membership changes over time
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Users className="w-3.5 h-3.5" />
          <span>{totalActivities} activities</span>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <Loader2 className="w-5 h-5 mx-auto mb-2 text-gray-400 animate-spin" />
            <p className="text-sm font-medium">Loading activities...</p>
            <p className="text-xs">
              Fetching contact's segment activity history
            </p>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-destructive">
            <Users className="w-8 h-8 mx-auto mb-2 text-destructive/50" />
            <p className="text-sm font-medium">Error loading activities</p>
            <p className="text-xs">{error}</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium">No activity yet</p>
            <p className="text-xs">
              This contact hasn't entered or exited any segments.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {activities.map((activity, index) => {
              const { date, time } = formatTimestamp(activity.timestamp);
              const isLast = index === activities.length - 1;

              return (
                <div
                  key={activity.id}
                  className="p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Timeline indicator */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`p-1.5 rounded-full border-2 ${getActivityColor(activity.type)}`}
                      >
                        {getActivityIcon(activity.type)}
                      </div>
                      {!isLast && (
                        <div className="w-0.5 h-5 bg-gray-200 mt-1"></div>
                      )}
                    </div>

                    {/* Activity content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActivityColor(activity.type)} ${getActivityTextColor(activity.type)}`}
                          >
                            {activity.type === 'add' ? 'Added' : 'Removed'}
                          </span>
                          <span className="font-medium text-gray-900 text-sm">
                            {activity.segmentName}
                          </span>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-3">
                          <Calendar className="w-3.5 h-3.5" />
                          <div className="text-right">
                            <div className="font-medium">{date}</div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{time}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTab;
